import { useMemo } from 'react';
import { Calendar as BigCalendar, dayjsLocalizer, Event } from 'react-big-calendar';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Spin } from '@bancobarigui/barihaus';
import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { useCalendarData } from './useCalendarData';
import { CalendarToolbar } from './components/CalendarToolbar';
import { CalendarFilters } from './components/CalendarFilters';
import { MonthDateCellWrapper } from './components/MonthDateCellWrapper';
import { DetailInspection } from '@/components/DetailInspection';
import { STATUS_COLORS, CalendarEvent } from './types';
import './calendar.css';

// Configurar dayjs para português
dayjs.locale('pt-br');

const localizer = dayjsLocalizer(dayjs);

// Mensagens em português para o calendário
const messages = {
  allDay: 'Dia inteiro',
  previous: 'Anterior',
  next: 'Próximo',
  today: 'Hoje',
  month: 'Mês',
  week: 'Semana',
  day: 'Dia',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'Não há vistorias agendadas neste período.',
  showMore: (total: number) => `+ ${total} mais`,
};

export default function Calendar() {
  const {
    events,
    loading,
    currentDate,
    view,
    filters,
    selectedEvent,
    drawerVisible,
    userRole,
    user,
    handleNavigate,
    handleViewChange,
    handleFilterChange,
    handleEventSelect,
    handleDrawerClose,
    handleRefresh,
    handleToday,
    handlePrevious,
    handleNext,
    handleDateSelect,
  } = useCalendarData();

  // Estilizar eventos baseado no status
  const eventStyleGetter = (event: CalendarEvent) => {
    const statusColor = STATUS_COLORS[event.status];

    // Fallback caso o status não esteja mapeado
    if (!statusColor) {
      return {
        style: {
          backgroundColor: '#f0f0f0',
          borderColor: '#d9d9d9',
          color: '#000000',
          borderRadius: '4px',
          border: '1px solid #d9d9d9',
          padding: '2px 5px',
          fontSize: '12px',
          fontWeight: 500,
        },
      };
    }

    return {
      style: {
        backgroundColor: statusColor.bg,
        borderColor: statusColor.border,
        color: statusColor.text,
        borderRadius: '4px',
        border: `1px solid ${statusColor.border}`,
        padding: '2px 5px',
        fontSize: '12px',
        fontWeight: 500,
      },
    };
  };

  // Componentes customizados para o calendário
  const components = useMemo(
    () => ({
      toolbar: () => null, // Usar nossa toolbar customizada
      month: {
        dateHeader: ({ date, label }: any) => (
          <MonthDateCellWrapper value={date} events={events}>
            <span>{label}</span>
          </MonthDateCellWrapper>
        ),
      },
    }),
    [events]
  );

  return (
    <ProtectedRouteContainer
      pageHeader={<PageHeader title="Calendário" />}
    >
      <FeatureAlert services={['inspection']} />
      <ServiceCheck services={['inspection']} featureName="Calendario">
        <div className="calendar-page">
        {/* Toolbar e Filtros na mesma linha */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <CalendarToolbar
              currentDate={currentDate}
              view={view}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onToday={handleToday}
              onDateSelect={handleDateSelect}
            />
          </div>

          <div>
            <CalendarFilters
              userRole={userRole as 'bank' | 'master_provider' | 'operational_provider' | 'inspector' | null}
              user={user}
              filters={filters}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        {/* Calendário */}
        <div className="calendar-container">
          <Spin spinning={loading}>
            <BigCalendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 'calc(100vh - 310px)', minHeight: '600px' }}
              view={view}
              onView={handleViewChange}
              date={currentDate}
              onNavigate={handleNavigate}
              onSelectEvent={handleEventSelect}
              eventPropGetter={eventStyleGetter}
              messages={messages}
              components={components}
              popup
              selectable={false}
              formats={{
                monthHeaderFormat: (date) => dayjs(date).format('MMMM YYYY'),
                dayHeaderFormat: (date) => dayjs(date).format('dddd, DD/MM'),
                dayRangeHeaderFormat: ({ start, end }) =>
                  `${dayjs(start).format('DD/MM')} - ${dayjs(end).format('DD/MM/YYYY')}`,
                agendaDateFormat: (date) => dayjs(date).format('DD/MM/YYYY'),
                agendaTimeFormat: (date) => dayjs(date).format('HH:mm'),
                agendaTimeRangeFormat: ({ start, end }) =>
                  `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}`,
                eventTimeRangeFormat: ({ start, end }) =>
                  `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}`,
              }}
            />
          </Spin>
        </div>

        {/* Modal de detalhes da inspeção */}
        <DetailInspection
          open={drawerVisible}
          inspectionId={selectedEvent?.inspectionItemId ?? null}
          itemId={selectedEvent?.inspectionItemId}
          onClose={handleDrawerClose}
          onUpdate={handleRefresh}
        />
        </div>
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
